import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Candidate, JobOffer } from '../../models/job';
import {
  JobStatus,
  type CandidateAssessmentSummary,
  type CandidateQuestionsAnswersResponse,
} from '../../types/job.types';
import { ExamConfig } from '../../types/exam-config.types';

interface DemoModeState {
  isDemoMode: boolean;
  mockJob: JobOffer | null;
  mockCandidates: Candidate[] | null;
  mockAssessment: CandidateAssessmentSummary | null;
  mockAssessmentsByCandidateId: Record<string, CandidateAssessmentSummary>;
  mockExamConfig: ExamConfig | null;
  startDemo: () => void;
  endDemo: () => void;
}

const DemoModeContext = createContext<DemoModeState | undefined>(undefined);

/** Job id used in `/job/:jobId` when demo / guided-tour sample data is active. */
export const DEMO_JOB_ID = 'demo-job-001';

const nowIso = () => new Date().toISOString();

function createMockJob(): JobOffer {
  const createdAt = nowIso();
  return {
    id: DEMO_JOB_ID,
    title: 'Senior Frontend Engineer (React + TypeScript)',
    description:
      'Build and scale recruiter-facing and candidate-facing product experiences for an AI-assisted hiring platform.',
    educationRequirements: "Master's Degree in Computer Science or equivalent",
    location: 'Paris, France (Hybrid)',
    remotePolicy: 'hybrid',
    contractType: 'CDI',
    yearsOfExperience: 5,
    requiredDegree: "Master's Degree in Computer Science or equivalent",
    companyName: 'Invirtus Labs',
    companySector: 'HR Technology / SaaS',
    companyDescription:
      'Invirtus builds AI-assisted hiring tools that help recruiters screen candidates faster and run structured interviews at scale. The team ships product features end-to-end with a strong focus on UX quality, reliability, and privacy.',
    mission_role_in_job:
      'Own the recruiter experience for candidate pipelines: dashboards, job details, candidate tables, and interview flows. Partner with design and backend to ship accessible React + TypeScript UIs, improve performance (Core Web Vitals), and maintain a consistent component system. Participate in code reviews, mentor junior engineers, and help define technical tradeoffs for new features.',
    codingLanguages: [
      { name: 'TypeScript', importance: 'must_have' },
      { name: 'JavaScript', importance: 'must_have' },
      { name: 'SQL', importance: 'nice_to_have' },
    ],
    frameworksLibraries: [
      { name: 'React', importance: 'must_have' },
      { name: 'Next.js', importance: 'nice_to_have' },
      { name: 'Tailwind CSS', importance: 'nice_to_have' },
    ],
    businessKnowledge: [{ name: 'HR Tech workflows', importance: 'nice_to_have' }],
    softSkills: [
      { name: 'Stakeholder communication', importance: 'must_have' },
      { name: 'Product ownership', importance: 'must_have' },
    ],
    customInstructions:
      'Prioritize candidates with strong React architecture experience and proven dashboard UX optimization outcomes.',
    isActive: true,
    status: JobStatus.ACTIVE,
    createdById: 'demo-user',
    creator_email: 'recruiter@invirtus.com',
    createdAt,
    updatedAt: createdAt,
    documents: [],
    authorizeCvUpload: true,
    enableCvAutoInterview: true,
    cvInterviewThreshold: 82,
    brief_exam: {
      technical: 'Focus on React architecture, TypeScript quality, and product tradeoff communication.',
    },
    candidateCount: 10,
    requiresReview: false,
    source: 'MANUAL',
  };
}

function createMockCandidates(): Candidate[] {
  const createdAt = nowIso();
  return [
    {
      id: 'demo-candidate-001',
      talentId: 'demo-talent-001',
      jobOfferId: 'demo-job-001',
      invitationToken: 'demo-invite-token-001',
      pipelineStageSemantic: 'pending',
      score: 9200,
      talentName: 'Sarah Johnson',
      talentEmail: 'sarah.johnson@example.com',
      talentLinkedinUrl: 'https://www.linkedin.com/in/sarah-johnson-fe',
      createdAt,
      updatedAt: createdAt,
      hasAssessmentAvailable: false,
      hasValidEmail: true,
      enhancedData: {
        extracted_info: {
          email: 'sarah.johnson@example.com',
          first_name: 'Sarah',
          last_name: 'Johnson',
          title: 'Senior Frontend Engineer',
          technical_skills: ['React', 'TypeScript', 'System Design', 'Testing', 'TanStack Query'],
          languages: ['English', 'French'],
          education: ['MSc Computer Science - Sorbonne University'],
          strengths: [
            'Designed scalable recruiter dashboards used by 40+ hiring teams.',
            'Improved page performance and reduced bundle size by 24%.',
          ],
          weaknesses: ['Limited direct experience with psychometric tooling integration.'],
          experience: [
            {
              job_title: 'Senior Frontend Engineer',
              company: 'TalentScale',
              duration: 'Jan 2021 - Present',
            },
            {
              job_title: 'Frontend Engineer',
              company: 'HR Flow',
              duration: 'Jun 2018 - Dec 2020',
            },
          ],
          total_experience: {
            years: 7,
            months: 6,
            total_months: 90,
          },
          years_of_experience: 7,
          match_score: 92,
        },
      },
    },
    {
      id: 'demo-candidate-002',
      talentId: 'demo-talent-002',
      jobOfferId: 'demo-job-001',
      invitationToken: 'demo-invite-token-002',
      pipelineStageSemantic: 'pending',
      score: 7800,
      talentName: 'Marcus Chen',
      talentEmail: 'marcus.chen@example.com',
      talentLinkedinUrl: 'https://www.linkedin.com/in/marcus-chen-fe',
      createdAt,
      updatedAt: createdAt,
      hasAssessmentAvailable: false,
      hasValidEmail: true,
      enhancedData: {
        extracted_info: {
          email: 'marcus.chen@example.com',
          first_name: 'Marcus',
          last_name: 'Chen',
          title: 'Frontend Platform Engineer',
          technical_skills: ['React', 'Node.js', 'CI/CD', 'Performance', 'Design Systems'],
          languages: ['English'],
          education: ['BEng Software Engineering - McGill University'],
          strengths: ['Strong platform engineering background.', 'Excellent CI/CD and release discipline.'],
          weaknesses: ['Less product discovery exposure than role target.'],
          experience: [
            {
              job_title: 'Frontend Platform Engineer',
              company: 'ScaleWorks',
              duration: 'Mar 2020 - Present',
            },
          ],
          total_experience: {
            years: 5,
            months: 2,
            total_months: 62,
          },
          years_of_experience: 5,
          match_score: 78,
        },
      },
    },
    {
      id: 'demo-candidate-003',
      talentId: 'demo-talent-003',
      jobOfferId: 'demo-job-001',
      invitationToken: 'demo-invite-token-003',
      pipelineStageSemantic: 'pending',
      score: 5500,
      talentName: 'Amelia Dubois',
      talentEmail: 'amelia.dubois@example.com',
      talentLinkedinUrl: 'https://www.linkedin.com/in/amelia-dubois-ui',
      createdAt,
      updatedAt: createdAt,
      hasAssessmentAvailable: false,
      hasValidEmail: true,
      enhancedData: {
        extracted_info: {
          email: 'amelia.dubois@example.com',
          first_name: 'Amelia',
          last_name: 'Dubois',
          title: 'Frontend Developer',
          technical_skills: ['JavaScript', 'CSS', 'Accessibility', 'REST APIs'],
          languages: ['French', 'English'],
          education: ['BSc Information Systems - Universite de Lyon'],
          strengths: ['Accessibility-first implementation habits.', 'Strong cross-functional collaboration.'],
          weaknesses: ['Limited exposure to advanced TypeScript patterns.'],
          experience: [
            {
              job_title: 'Frontend Developer',
              company: 'NovaHire',
              duration: 'May 2021 - Present',
            },
          ],
          total_experience: {
            years: 3,
            months: 10,
            total_months: 46,
          },
          years_of_experience: 3,
          match_score: 55,
        },
      },
    },
    {
      id: 'demo-candidate-004',
      talentId: 'demo-talent-004',
      jobOfferId: 'demo-job-001',
      invitationToken: 'demo-invite-token-004',
      pipelineStageSemantic: 'interviewing',
      score: 7400,
      talentName: 'Lina Moreau',
      talentEmail: 'lina.moreau@example.com',
      talentLinkedinUrl: 'https://www.linkedin.com/in/lina-moreau',
      interviewInvitationToken: 'demo-interview-token-004',
      interviewId: 'demo-interview-004',
      interviewStatus: 'sent',
      interviewExpiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 3).toISOString(),
      createdAt,
      updatedAt: createdAt,
      hasAssessmentAvailable: false,
      hasValidEmail: true,
      enhancedData: {
        extracted_info: {
          email: 'lina.moreau@example.com',
          first_name: 'Lina',
          last_name: 'Moreau',
          title: 'Frontend Engineer',
          technical_skills: ['React', 'TypeScript', 'UI Testing'],
          languages: ['French', 'English'],
          education: ['BSc Computer Science - EPITA'],
          strengths: ['Strong execution speed in UI delivery.', 'Reliable cross-team communication.'],
          weaknesses: ['Needs deeper experience with large-scale frontend architecture.'],
          experience: [
            {
              job_title: 'Frontend Engineer',
              company: 'FlowRecruit',
              duration: 'Aug 2020 - Present',
            },
          ],
          total_experience: {
            years: 4,
            months: 4,
            total_months: 52,
          },
          years_of_experience: 4,
          match_score: 74,
        },
      },
    },
    {
      id: 'demo-candidate-101',
      talentId: 'demo-talent-101',
      jobOfferId: 'demo-job-001',
      invitationToken: 'demo-invite-token-101',
      pipelineStageSemantic: 'interviewed',
      score: 9000,
      talentName: 'Noah Martin',
      talentEmail: 'noah.martin@example.com',
      talentLinkedinUrl: 'https://www.linkedin.com/in/noah-martin-fe',
      interviewInvitationToken: 'demo-interview-token-101',
      interviewId: 'demo-interview-101',
      interviewStatus: 'completed',
      interviewExpiresAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
      createdAt,
      updatedAt: createdAt,
      hasAssessmentAvailable: true,
      hasValidEmail: true,
      enhancedData: {
        extracted_info: {
          email: 'noah.martin@example.com',
          first_name: 'Noah',
          last_name: 'Martin',
          title: 'Senior Frontend Engineer',
          technical_skills: ['React', 'TypeScript', 'Architecture'],
          languages: ['English', 'French'],
          education: ['MSc Software Engineering - University of Amsterdam'],
          strengths: ['Excellent architecture decisions under pressure.', 'Strong ownership and mentoring.'],
          weaknesses: ['Can over-optimize early in discovery phases.'],
          experience: [
            {
              job_title: 'Senior Frontend Engineer',
              company: 'BrightHire',
              duration: 'Jan 2019 - Present',
            },
            {
              job_title: 'Frontend Engineer',
              company: 'ScalePortal',
              duration: 'Jun 2016 - Dec 2018',
            },
          ],
          total_experience: {
            years: 8,
            months: 2,
            total_months: 98,
          },
          years_of_experience: 8,
          match_score: 90,
        },
      },
    },
    {
      id: 'demo-candidate-102',
      talentId: 'demo-talent-102',
      jobOfferId: 'demo-job-001',
      invitationToken: 'demo-invite-token-102',
      pipelineStageSemantic: 'interviewed',
      score: 7600,
      talentName: 'Yasmine Haddad',
      talentEmail: 'yasmine.haddad@example.com',
      talentLinkedinUrl: 'https://www.linkedin.com/in/yasmine-haddad',
      interviewInvitationToken: 'demo-interview-token-102',
      interviewId: 'demo-interview-102',
      interviewStatus: 'completed',
      interviewExpiresAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
      createdAt,
      updatedAt: createdAt,
      hasAssessmentAvailable: true,
      hasValidEmail: true,
      enhancedData: {
        extracted_info: {
          email: 'yasmine.haddad@example.com',
          first_name: 'Yasmine',
          last_name: 'Haddad',
          title: 'Frontend Engineer',
          technical_skills: ['React', 'CSS', 'Performance'],
          languages: ['Arabic', 'French', 'English'],
          education: ['BSc Computer Engineering - ENSA'],
          strengths: ['Strong pragmatic delivery and communication.', 'Great UI polish and performance tuning.'],
          weaknesses: ['Needs more depth in system-level architecture.'],
          experience: [
            {
              job_title: 'Frontend Engineer',
              company: 'TalentOps',
              duration: 'May 2020 - Present',
            },
          ],
          total_experience: {
            years: 5,
            months: 1,
            total_months: 61,
          },
          years_of_experience: 5,
          match_score: 76,
        },
      },
    },
    {
      id: 'demo-candidate-103',
      talentId: 'demo-talent-103',
      jobOfferId: 'demo-job-001',
      invitationToken: 'demo-invite-token-103',
      pipelineStageSemantic: 'interviewed',
      score: 6200,
      talentName: 'Karim Saidi',
      talentEmail: 'karim.saidi@example.com',
      talentLinkedinUrl: 'https://www.linkedin.com/in/karim-saidi',
      interviewInvitationToken: 'demo-interview-token-103',
      interviewId: 'demo-interview-103',
      interviewStatus: 'completed',
      interviewExpiresAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4).toISOString(),
      createdAt,
      updatedAt: createdAt,
      hasAssessmentAvailable: true,
      hasValidEmail: true,
      enhancedData: {
        extracted_info: {
          email: 'karim.saidi@example.com',
          first_name: 'Karim',
          last_name: 'Saidi',
          title: 'Junior Frontend Developer',
          technical_skills: ['JavaScript', 'HTML', 'CSS'],
          languages: ['French', 'English'],
          education: ['DUT Informatique - Universite de Strasbourg'],
          strengths: ['Learns quickly and follows feedback well.', 'Solid accessibility basics.'],
          weaknesses: ['Limited practical experience with complex product domains.'],
          experience: [
            {
              job_title: 'Junior Frontend Developer',
              company: 'UIForge',
              duration: 'Jul 2022 - Present',
            },
          ],
          total_experience: {
            years: 2,
            months: 6,
            total_months: 30,
          },
          years_of_experience: 2,
          match_score: 62,
        },
      },
    },
    {
      id: 'demo-candidate-201',
      talentId: 'demo-talent-201',
      jobOfferId: 'demo-job-001',
      invitationToken: 'demo-invite-token-201',
      pipelineStageSemantic: 'accepted',
      score: 8800,
      talentName: 'Emma Laurent',
      talentEmail: 'emma.laurent@example.com',
      interviewInvitationToken: 'demo-interview-token-201',
      interviewId: 'demo-interview-201',
      interviewStatus: 'completed',
      interviewExpiresAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
      createdAt,
      updatedAt: createdAt,
      hasAssessmentAvailable: true,
      hasValidEmail: true,
      enhancedData: {
        extracted_info: {
          email: 'emma.laurent@example.com',
          first_name: 'Emma',
          last_name: 'Laurent',
          title: 'Senior Frontend Engineer',
          technical_skills: ['React', 'TypeScript', 'Testing'],
          languages: ['English', 'Spanish'],
          education: ['MSc Human-Computer Interaction - UCL'],
          strengths: ['Strong test strategy and high release quality.', 'Clear product/engineering alignment.'],
          weaknesses: ['Sometimes too conservative in technology choices.'],
          experience: [
            {
              job_title: 'Senior Frontend Engineer',
              company: 'PeopleFlow',
              duration: 'Feb 2018 - Present',
            },
          ],
          total_experience: {
            years: 7,
            months: 0,
            total_months: 84,
          },
          years_of_experience: 7,
          match_score: 88,
        },
      },
    },
    {
      id: 'demo-candidate-202',
      talentId: 'demo-talent-202',
      jobOfferId: 'demo-job-001',
      invitationToken: 'demo-invite-token-202',
      pipelineStageSemantic: 'rejected',
      score: 5800,
      talentName: 'Leo Bernard',
      talentEmail: 'leo.bernard@example.com',
      interviewInvitationToken: 'demo-interview-token-202',
      interviewId: 'demo-interview-202',
      interviewStatus: 'completed',
      interviewExpiresAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
      createdAt,
      updatedAt: createdAt,
      hasAssessmentAvailable: true,
      hasValidEmail: true,
      enhancedData: {
        extracted_info: {
          email: 'leo.bernard@example.com',
          first_name: 'Leo',
          last_name: 'Bernard',
          title: 'Frontend Developer',
          technical_skills: ['JavaScript', 'React'],
          languages: ['French'],
          education: ['BSc Digital Product Development - Efrei'],
          strengths: ['Strong collaboration and responsiveness to feedback.'],
          weaknesses: ['Inconsistent depth in advanced technical problem solving.'],
          experience: [
            {
              job_title: 'Frontend Developer',
              company: 'QuickApps',
              duration: 'Jan 2021 - Present',
            },
          ],
          total_experience: {
            years: 3,
            months: 3,
            total_months: 39,
          },
          years_of_experience: 3,
          match_score: 58,
        },
      },
    },
    {
      id: 'demo-candidate-203',
      talentId: 'demo-talent-203',
      jobOfferId: 'demo-job-001',
      invitationToken: 'demo-invite-token-203',
      pipelineStageSemantic: 'hired',
      score: 9300,
      talentName: 'Sofia El Amrani',
      talentEmail: 'sofia.elamrani@example.com',
      interviewInvitationToken: 'demo-interview-token-203',
      interviewId: 'demo-interview-203',
      interviewStatus: 'completed',
      interviewExpiresAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6).toISOString(),
      createdAt,
      updatedAt: createdAt,
      hasAssessmentAvailable: true,
      hasValidEmail: true,
      enhancedData: {
        extracted_info: {
          email: 'sofia.elamrani@example.com',
          first_name: 'Sofia',
          last_name: 'El Amrani',
          title: 'Lead Frontend Engineer',
          technical_skills: ['React', 'TypeScript', 'Leadership', 'Architecture'],
          languages: ['Arabic', 'French', 'English'],
          education: ['MSc Software Systems - Polytechnique Montreal'],
          strengths: ['Excellent leadership and architecture decisions.', 'Strong delivery under constraints.'],
          weaknesses: ['Can delegate too late in fast-moving sprints.'],
          experience: [
            {
              job_title: 'Lead Frontend Engineer',
              company: 'HireSphere',
              duration: 'Apr 2017 - Present',
            },
          ],
          total_experience: {
            years: 9,
            months: 1,
            total_months: 109,
          },
          years_of_experience: 9,
          match_score: 93,
        },
      },
    },
  ];
}

function createMockAssessment(
  candidateId: string,
  interviewId: string,
  fitScore: number,
  techScore: number,
  softScore: number,
  mbtiType: string
): CandidateAssessmentSummary {
  return {
    candidate_id: candidateId,
    interview_id: interviewId,
    personality_traits: ['strategic', 'decisive', 'collaborative'],
    final_recommendation: 'Strong hire recommendation for senior frontend ownership.',
    fit_score: fitScore,
    is_processing: false,
    technical_assessment: {
      overall_tech_percent: techScore,
      overall_score: techScore,
      recommendation: 'Excellent technical depth for the target role.',
      strengths: ['React architecture', 'TypeScript quality', 'Debugging'],
      weaknesses: ['Could improve low-level algorithm explanation speed.'],
      skill_breakdown: {
        core_concepts_strengths: { level: 9, explanation: 'Solid component architecture and state modeling.' },
        core_concepts_weaknesses: { level: 8, explanation: 'Handled edge cases with clear tradeoffs.' },
        algorithmic: { level: 7, explanation: 'Good correctness and clarity.' },
        clean_code: { level: 9, explanation: 'Readable and maintainable code style.' },
        debugging: { level: 9, explanation: 'Fast root-cause analysis.' },
        tradeoffs: { level: 8, explanation: 'Balanced performance and maintainability.' },
        ai_tools: { level: 8, explanation: 'Uses AI tools responsibly in workflow.' },
      },
    },
    soft_skills_assessment: {
      overall_soft_skills_percent: softScore,
      overall_score: softScore,
      recommendation: 'Strong communication and stakeholder alignment potential.',
      strengths: ['Communication', 'Team spirit', 'Self-awareness'],
      weaknesses: ['Could provide shorter executive summaries.'],
      skill_breakdown: {
        communication: { level: 9, explanation: 'Clear and concise responses.' },
        team_spirit: { level: 8, explanation: 'Collaborative approach with examples.' },
        growth_mindset: { level: 8, explanation: 'Demonstrates reflective learning behavior.' },
        self_awareness: { level: 9, explanation: 'Accurate self-assessment and ownership.' },
        adaptability: { level: 8, explanation: 'Comfortable with shifting priorities.' },
      },
    },
    fit_assessment: {
      fit_score: fitScore,
      executive_summary: 'Profile aligns strongly with role expectations and team context.',
      key_strengths_aligning_with_role: [
        'Strong frontend architecture leadership',
        'High code quality and testing discipline',
      ],
      areas_of_concern_and_development: ['Limited direct psychometric tooling exposure'],
      specific_recommendations_for_hiring: ['Proceed to final manager round'],
      risk_assessment_and_mitigation_stratgies: 'Low risk; assign onboarding buddy on domain specifics.',
      expected_onboarding_and_rampup_timeline: '2-4 weeks',
    },
    language_assessment: {
      cefr_level: 'C1',
      language_feedback: 'Strong spoken and written communication.',
      spoken_language: 'English',
    },
    scoring_version: 'v3',
    has_cheating_flag: false,
    psychometric_results: {
      mbti: {
        type: mbtiType,
        scores: { E: 18, I: 12, N: 20, S: 10, T: 17, F: 13, J: 19, P: 11 },
        percentages: { E: 60, I: 40, N: 67, S: 33, T: 57, F: 43, J: 63, P: 37 },
        description: 'Strategic, decisive, and comfortable driving execution in a fast-paced product environment.',
        title: 'Commander',
        executive_summary: 'Confident organizer who thrives in structured execution environments.',
        type_explanation: {
          E: 'Energy from collaboration and active discussions.',
          N: 'Pattern-driven and future-oriented thinking.',
        },
        strengths: ['Decisive planning', 'Leadership in ambiguity'],
        watch_outs: ['May over-index on speed versus consensus'],
      },
      disc: {
        scores: { D: 12, I: 8, S: 6, C: 4 },
        percentages: { D: 38, I: 28, S: 20, C: 14 },
        primary: 'D',
        secondary: 'I',
        language: 'en',
        title: 'Dominance / Influence',
        description:
          'Direct, results-driven style with strong people energy — moves fast and brings others along.',
        strengths: ['Decisive under pressure', 'Builds momentum in teams'],
        challenges: ['May move before full alignment', 'Can overlook detail depth'],
      },
    },
  };
}

function createMockAssessmentsByCandidateId(): Record<string, CandidateAssessmentSummary> {
  const interviewedHigh = createMockAssessment('demo-candidate-101', 'demo-interview-101', 91, 93, 89, 'ENTJ');
  const interviewedMedium = createMockAssessment('demo-candidate-102', 'demo-interview-102', 52, 55, 49, 'ENFJ');
  const interviewedLow = createMockAssessment('demo-candidate-103', 'demo-interview-103', 33, 35, 31, 'ISFP');
  const accepted = createMockAssessment('demo-candidate-201', 'demo-interview-201', 88, 90, 86, 'INTJ');
  const rejected = createMockAssessment('demo-candidate-202', 'demo-interview-202', 57, 55, 60, 'ISTP');
  const hired = createMockAssessment('demo-candidate-203', 'demo-interview-203', 94, 95, 92, 'ENTP');

  return {
    [interviewedHigh.candidate_id]: interviewedHigh,
    [interviewedMedium.candidate_id]: interviewedMedium,
    [interviewedLow.candidate_id]: interviewedLow,
    [accepted.candidate_id]: accepted,
    [rejected.candidate_id]: rejected,
    [hired.candidate_id]: hired,
  };
}

function createMockExamConfig(): ExamConfig {
  return {
    id: 'demo-exam-config-001',
    job_offer_id: 'demo-job-001',
    version: 3,
    language: 'EN',
    interview_expiry_days: 7,
    created_at: nowIso(),
    updated_at: nowIso(),
    config_data: {
      sections: [
        {
          id: 'section-technical',
          name: 'Technical Skills',
          skills: [
            {
              id: 'skill-react-architecture',
              name: 'React Architecture',
              questions: [
                {
                  id: 'q-tech-1',
                  text: 'How would you structure state management for a recruiter dashboard with heavy filtering?',
                  type: 'spoken',
                  timeMinutes: 4,
                },
                {
                  id: 'q-tech-2',
                  text: 'Refactor this component to improve maintainability and rendering performance.',
                  type: 'code-write',
                  timeMinutes: 8,
                  codingLanguage: 'TypeScript',
                },
              ],
            },
          ],
        },
        {
          id: 'section-soft-skills',
          name: 'Soft Skills',
          skills: [
            {
              id: 'skill-stakeholder-communication',
              name: 'Stakeholder Communication',
              questions: [
                {
                  id: 'q-soft-1',
                  text: 'Describe a time you aligned product and engineering on conflicting priorities.',
                  type: 'spoken',
                  timeMinutes: 4,
                },
              ],
            },
          ],
        },
      ],
      psychometric_sections: [
        {
          test_id: 'mbti-v1',
          test_slug: 'mbti',
          test_name: 'MBTI Personality Inventory',
          enabled: true,
        },
      ],
    },
  };
}

function createMockQuestionsAnswersByCandidateId(): Record<string, CandidateQuestionsAnswersResponse> {
  const byCandidate = (
    candidateId: string,
    candidateName: string,
    candidateEmail: string,
    interviewId: string,
    interviewStatus: string,
    level: 'high' | 'medium' | 'low'
  ): CandidateQuestionsAnswersResponse => {
    const qualityMap = {
      high: {
        scoreWord: 'high-confidence',
        answerSuffix: 'The answer is structured, specific, and demonstrates strong senior-level reasoning.',
      },
      medium: {
        scoreWord: 'balanced',
        answerSuffix: 'The answer is clear and practical with room for deeper architectural detail.',
      },
      low: {
        scoreWord: 'basic',
        answerSuffix: 'The answer covers fundamentals but lacks depth and tradeoff analysis.',
      },
    } as const;

    return {
      candidate_id: candidateId,
      candidate_name: candidateName,
      candidate_email: candidateEmail,
      interview_id: interviewId,
      interview_status: interviewStatus,
      questions_answers: [
        {
          question_text:
            'Describe how you would design a scalable candidate table for thousands of rows with filtering and sorting.',
          question_type: 'spoken',
          question_kind: 'behavioral',
          candidate_answer: `${qualityMap[level].scoreWord} response: ${qualityMap[level].answerSuffix}`,
          candidate_code: '',
          time_allocated_minutes: 4,
          time_spent_seconds: 208,
          skill_area: 'System Design',
          section: 'Technical Skills',
        },
        {
          question_text: 'Implement a reusable debounce hook in React with TypeScript.',
          question_type: 'code-write',
          question_kind: 'technical',
          criteria: 'Clean API, proper cleanup, typed arguments.',
          candidate_answer:
            'I used useEffect cleanup and a generic signature so the hook is reusable across search and form flows.',
          candidate_code:
            "import { useEffect, useState } from 'react';\n\nexport function useDebounce<T>(value: T, delay = 300): T {\n  const [debounced, setDebounced] = useState(value);\n\n  useEffect(() => {\n    const id = window.setTimeout(() => setDebounced(value), delay);\n    return () => window.clearTimeout(id);\n  }, [value, delay]);\n\n  return debounced;\n}",
          coding_language: 'typescript',
          time_allocated_minutes: 8,
          time_spent_seconds: 401,
          skill_area: 'React & TypeScript',
          section: 'Technical Skills',
        },
      ],
    };
  };

  return {
    'demo-candidate-101': byCandidate(
      'demo-candidate-101',
      'Noah Martin',
      'noah.martin@example.com',
      'demo-interview-101',
      'completed',
      'high'
    ),
    'demo-candidate-102': byCandidate(
      'demo-candidate-102',
      'Yasmine Haddad',
      'yasmine.haddad@example.com',
      'demo-interview-102',
      'completed',
      'medium'
    ),
    'demo-candidate-103': byCandidate(
      'demo-candidate-103',
      'Karim Saidi',
      'karim.saidi@example.com',
      'demo-interview-103',
      'completed',
      'low'
    ),
    'demo-candidate-201': byCandidate(
      'demo-candidate-201',
      'Emma Laurent',
      'emma.laurent@example.com',
      'demo-interview-201',
      'completed',
      'high'
    ),
    'demo-candidate-202': byCandidate(
      'demo-candidate-202',
      'Leo Bernard',
      'leo.bernard@example.com',
      'demo-interview-202',
      'completed',
      'low'
    ),
    'demo-candidate-203': byCandidate(
      'demo-candidate-203',
      'Sofia El Amrani',
      'sofia.elamrani@example.com',
      'demo-interview-203',
      'completed',
      'high'
    ),
  };
}

export const DemoModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [mockJob, setMockJob] = useState<JobOffer | null>(null);
  const [mockCandidates, setMockCandidates] = useState<Candidate[] | null>(null);
  const [mockAssessment, setMockAssessment] = useState<CandidateAssessmentSummary | null>(null);
  const [mockAssessmentsByCandidateId, setMockAssessmentsByCandidateId] = useState<
    Record<string, CandidateAssessmentSummary>
  >({});
  const [mockExamConfig, setMockExamConfig] = useState<DemoModeState['mockExamConfig']>(null);
  const originalFetchRef = useRef<typeof window.fetch | null>(null);
  const demoModeRef = useRef(false);
  const mockAssessmentsRef = useRef<Record<string, CandidateAssessmentSummary>>({});
  const mockExamConfigRef = useRef<ExamConfig | null>(null);
  const mockQuestionsAnswersRef = useRef<Record<string, CandidateQuestionsAnswersResponse>>({});

  useEffect(() => {
    demoModeRef.current = isDemoMode;
  }, [isDemoMode]);

  useEffect(() => {
    mockAssessmentsRef.current = mockAssessmentsByCandidateId;
  }, [mockAssessmentsByCandidateId]);

  useEffect(() => {
    mockExamConfigRef.current = mockExamConfig;
  }, [mockExamConfig]);

  const installDemoFetchInterceptor = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (originalFetchRef.current) return;

    originalFetchRef.current = window.fetch.bind(window);
    const apiBase = String(import.meta.env.VITE_API_SERVER_URL ?? '');

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const currentIsDemo = demoModeRef.current;
      const originalFetch = originalFetchRef.current;
      if (!currentIsDemo || !originalFetch) {
        return originalFetch ? originalFetch(input, init) : fetch(input, init);
      }

      const rawUrl =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.toString()
            : input.url;
      const normalizedUrl = rawUrl.startsWith('http') && apiBase
        ? rawUrl
        : `${apiBase}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
      const method = (init?.method ?? (typeof input === 'object' && 'method' in input ? input.method : 'GET'))
        .toUpperCase();
      const assessmentMatch = normalizedUrl.match(/\/api\/candidates\/([^/]+)\/assessment$/);
      const examConfigMatch = normalizedUrl.match(/\/api\/jobs\/offers\/([^/]+)\/exam-config$/);
      const questionsAnswersMatch = normalizedUrl.match(/\/api\/interviews\/([^/]+)\/questions_answers$/);

      if (method === 'GET' && assessmentMatch) {
        const candidateId = assessmentMatch[1];
        const mock = mockAssessmentsRef.current[candidateId];
        if (mock) {
          return new Response(JSON.stringify(mock), {
            status: 200,
            headers: { 'Content-Type': 'application/json' },
          });
        }
      }

      if (examConfigMatch && examConfigMatch[1] === 'demo-job-001') {
        const examConfig = mockExamConfigRef.current;
        if (examConfig && (method === 'GET' || method === 'PUT')) {
          return new Response(JSON.stringify(examConfig), {
            status: 200,
            headers: { 'Content-Type': 'application/json' },
          });
        }
      }

      if (method === 'GET' && questionsAnswersMatch) {
        const candidateId = questionsAnswersMatch[1];
        const mockQa = mockQuestionsAnswersRef.current[candidateId];
        if (mockQa) {
          return new Response(JSON.stringify(mockQa), {
            status: 200,
            headers: { 'Content-Type': 'application/json' },
          });
        }
      }

      return originalFetch(input, init);
    };
  }, []);

  const uninstallDemoFetchInterceptor = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (!originalFetchRef.current) return;
    window.fetch = originalFetchRef.current;
    originalFetchRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      uninstallDemoFetchInterceptor();
    };
  }, [uninstallDemoFetchInterceptor]);

  const startDemo = useCallback(() => {
    setIsDemoMode(true);
    setMockJob(createMockJob());
    setMockCandidates(createMockCandidates());
    const assessmentsByCandidateId = createMockAssessmentsByCandidateId();
    setMockAssessmentsByCandidateId(assessmentsByCandidateId);
    mockQuestionsAnswersRef.current = createMockQuestionsAnswersByCandidateId();
    setMockAssessment(assessmentsByCandidateId['demo-candidate-101'] ?? null);
    setMockExamConfig(createMockExamConfig());
    installDemoFetchInterceptor();
  }, [installDemoFetchInterceptor]);

  const endDemo = useCallback(() => {
    setIsDemoMode(false);
    setMockJob(null);
    setMockCandidates(null);
    setMockAssessment(null);
    setMockAssessmentsByCandidateId({});
    mockQuestionsAnswersRef.current = {};
    setMockExamConfig(null);
    uninstallDemoFetchInterceptor();
  }, [uninstallDemoFetchInterceptor]);

  const value = useMemo(
    () => ({
      isDemoMode,
      mockJob,
      mockCandidates,
      mockAssessment,
      mockAssessmentsByCandidateId,
      mockExamConfig,
      startDemo,
      endDemo,
    }),
    [
      isDemoMode,
      mockJob,
      mockCandidates,
      mockAssessment,
      mockAssessmentsByCandidateId,
      mockExamConfig,
      startDemo,
      endDemo,
    ]
  );

  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
};

export function useDemoMode(): DemoModeState {
  const context = useContext(DemoModeContext);
  if (!context) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
}
